import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { defineMessages, useIntl } from 'react-intl';

import cx from 'classnames';
import config from '@plone/volto/registry';
import styles from './styles.module.css';
import type {
  QubicaContentProps,
  QubicaViewProps,
} from '@redturtle/qubicaamf-theme/components/View/Common/utils';
import {
  getAppendedResultsQuery,
  resetGetAppendedResults,
} from '@redturtle/qubicaamf-theme/actions/getAppendedResultsQuery';
import type { GetAppendedResultsQueryInitialState } from '@redturtle/qubicaamf-theme/reducers/getAppendedResultsQuery';
import Slides from './Slides';
import type { BlogExpanders } from '@redturtle/qubicaamf-theme/components/View/NewsTestimonialsView/NewsTestimonialView';
import MasonryWithEmphasizedContent from '@redturtle/qubicaamf-theme/components/commons/Masonry/MasonryWithEmphasizedContent';
import { useAppendedResults } from '@redturtle/qubicaamf-theme/helpers/hooks/useAppendedResults.hook';
import { useLocation } from 'react-router-dom';

export interface RootState {
  getAppendedResultsQuery: GetAppendedResultsQueryInitialState;
}

interface BlogProps extends Omit<QubicaViewProps, 'content'> {
  content: QubicaContentProps & {
    payoff?: string;
    '@components': BlogExpanders;
  };
}

export const queryData = {
  b_size: '6',
  sort_on: 'effective_date_with_priority',
  sort_order: 'descending',
  portal_type: ['News Item', 'Testimonial'],
};

const BlogHomepage = ({ content }: BlogProps) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const location = useLocation();

  const Container = config.getComponent('Container').component || 'div';

  const slides = content['@components'].blog_summary.news_macro_types || [];

  useEffect(() => {
    dispatch(
      getAppendedResultsQuery(
        '/',
        { ...queryData, b_start: 0 },
        content.UID,
        false,
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const data = useAppendedResults();

  const results = data.subrequests[content.UID];

  const loadMore = () => {
    dispatch(
      getAppendedResultsQuery(
        '/',
        { ...queryData, b_start: results.items.length },
        content.UID,
        true,
      ),
    );
  };

  useEffect(() => {
    resetGetAppendedResults(content.UID);
  }, [location.pathname, content.UID]);

  return (
    <Container>
      <div className={cx(styles.title)}>{content.payoff}</div>

      {!!results && results.items.length > 0 && (
        <MasonryWithEmphasizedContent
          data={results.items}
          loadMore={loadMore}
          totalItems={results.total}
          loading={results.loading}
          classNames="has--fullwidth--true has--bg--gray has--line_color--red has--add_bottom_line--true"
        />
      )}
      {!!results && results.error && (
        <div>{intl.formatMessage(messages.error_message)}</div>
      )}

      <div className={cx('has--fullwidth--true', styles.slidesWrapper)}>
        <Slides slides={slides} />
      </div>
    </Container>
  );
};

export { BlogHomepage };

const messages = defineMessages({
  error_message: {
    id: 'error_message',
    defaultMessage: 'There has been an error. Try refreshing the page.',
  },
});
